import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../services/api";
import { V2Icon } from "./V2App";

const FILTERS = {
    attention: () => true,
    critical: (item) => item.flags?.critical,
    waiting: (item) => item.flags?.waitingOver2h,
    unassigned: (item) => item.flags?.unassignedOver30m,
};

function getError(error) {
    return error?.response?.data?.error?.message || "Не удалось загрузить оперативную аналитику";
}

function formatMoment(value) {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function MetricCard({ code, label, value, hint, tone, active, onClick }) {
    return <button type="button" className={`v2-ai-metric ${tone || ""} ${active ? "active" : ""}`} onClick={() => onClick(code)} aria-pressed={active}>
        <span>{label}</span>
        <strong>{value ?? 0}</strong>
        <small>{hint}</small>
    </button>;
}

function TicketSignalCard({ item }) {
    return <article className={`v2-ai-ticket ${item.severity || "warning"}`}>
        <div className="v2-ai-ticket-rank" aria-label={`Приоритет сигнала: ${item.signalLabel}`}><i/><span>{item.signalLabel}</span></div>
        <div className="v2-ai-ticket-main">
            <header>
                <div>
                    <small>{item.ai?.topicLabel || "Другое"} · {item.company || "Компания не указана"}{item.terminalNumber ? ` · терминал ${item.terminalNumber}` : ""}</small>
                    <h3><Link to={`/v2/tickets/${item.id}`}>#{item.id} {item.title}</Link></h3>
                </div>
                <div className="v2-ai-ticket-owner"><small>Исполнитель</small><b>{item.assignee || "Не назначен"}</b></div>
            </header>
            <div className="v2-ai-reasons">{(item.reasons || []).map((reason) => <span className={reason.tone} key={reason.code}>{reason.label}</span>)}</div>
            <p className="v2-ai-summary">{item.ai?.summary || item.title}</p>
            {item.ai?.blockers ? <p className="v2-ai-blocker"><b>Блокирует:</b> {item.ai.blockers}</p> : null}
            <div className="v2-ai-next"><span>Следующий шаг</span><strong>{item.ai?.nextAction || "Проверить текущее состояние заявки."}</strong></div>
            <footer>
                <span>Статус: <b>{item.status}</b></span>
                <span>{item.waitingLabel ? <>ТП не отвечает: <b>{item.waitingLabel}</b></> : <>Обновлено: <b>{formatMoment(item.lastActivityAt)}</b></>}</span>
                <span className={`v2-ai-source ${item.ai?.fresh ? "fresh" : ""}`}>{item.ai?.available ? (item.ai.fresh ? "Hermes · актуально" : "Hermes · обновляется") : "Без AI-резюме"}</span>
                <Link className="v2-ai-open" to={`/v2/tickets/${item.id}`}>Открыть заявку <span aria-hidden="true">→</span></Link>
            </footer>
        </div>
    </article>;
}

export default function V2AiAnalytics() {
    const [state, setState] = useState({ loading: true, refreshing: false, error: "", data: null });
    const [filter, setFilter] = useState("attention");
    const load = useCallback(async (quiet = false) => {
        setState((current) => ({ ...current, loading: !quiet && !current.data, refreshing: quiet && Boolean(current.data), error: "" }));
        try {
            const { data } = await api.get("/api/v2/ai-analytics");
            setState({ loading: false, refreshing: false, error: "", data });
        } catch (error) {
            setState((current) => ({ ...current, loading: false, refreshing: false, error: getError(error) }));
        }
    }, []);

    useEffect(() => {
        load();
        const timer = window.setInterval(() => {
            if (document.visibilityState === "visible") load(true);
        }, 60000);
        return () => window.clearInterval(timer);
    }, [load]);

    const summary = state.data?.summary || {};
    const items = useMemo(() => (state.data?.items || []).filter(FILTERS[filter] || FILTERS.attention), [filter, state.data?.items]);
    const cards = [
        { code: "attention", label: "Нужно вмешаться", value: summary.attention, hint: "все сигналы сейчас", tone: summary.attention ? "warning" : "calm" },
        { code: "critical", label: "Критические", value: summary.critical, hint: "по данным и Hermes", tone: summary.critical ? "critical" : "calm" },
        { code: "waiting", label: "Без ответа > 2 ч", value: summary.waitingOver2h, hint: "партнёр ждёт ТП", tone: summary.waitingOver2h ? "high" : "calm" },
        { code: "unassigned", label: "Без исполнителя > 30 мин", value: summary.unassignedOver30m, hint: "нужно назначить", tone: summary.unassignedOver30m ? "warning" : "calm" },
    ];
    const focus = state.data?.focus;

    if (state.loading) return <div className="v2-page v2-ai-page"><div className="v2-page-state"><div className="v2-spinner"/><span>Hermes собирает картину по очереди…</span></div></div>;

    return <div className="v2-page v2-ai-page">
        <div className="v2-page-heading v2-ai-heading">
            <div><small>Оперативный контроль IT ТП</small><h1>ИИ Аналитика</h1><p>Минимум цифр — только то, где руководителю нужно вмешаться сейчас</p></div>
            <div className="v2-ai-heading-actions">
                <span>{state.data?.generatedAt ? `Обновлено ${formatMoment(state.data.generatedAt)}` : ""}</span>
                <button className="v2-button" disabled={state.refreshing} onClick={() => load(true)}><V2Icon name="refresh" size={17}/>{state.refreshing ? "Обновляем…" : "Обновить"}</button>
            </div>
        </div>

        {state.error ? <div className="v2-notice error"><span>{state.error}</span><button onClick={() => load(Boolean(state.data))}>Повторить</button></div> : null}

        {state.data ? <>
            <section className="v2-ai-metrics" aria-label="Ключевые сигналы">{cards.map((card) => <MetricCard key={card.code} {...card} active={filter === card.code} onClick={setFilter}/>)}</section>

            <section className={`v2-ai-focus ${focus?.tone || "calm"}`}>
                <div className="v2-ai-focus-icon"><V2Icon name="sparkles" size={23}/></div>
                <div><small>Фокус руководителя</small><h2>{focus?.title || "Очередь под контролем"}</h2><p>{focus?.description}</p>{focus?.nextAction ? <div><b>Что сделать:</b> {focus.nextAction}</div> : null}</div>
                {focus?.ticketId ? <Link to={`/v2/tickets/${focus.ticketId}`}>Перейти к заявке <span aria-hidden="true">→</span></Link> : null}
            </section>

            <div className="v2-ai-queue-head">
                <div><h2>Реагировать сейчас</h2><p>{filter === "attention" ? `Показано ${items.length} из ${state.data.total || 0} приоритетных заявок` : `По выбранному сигналу: ${items.length}`}</p></div>
                <div className="v2-ai-health"><span>Активных <b>{summary.active ?? 0}</b></span><span>SLA под угрозой <b>{summary.slaAtRisk ?? 0}</b></span><span>Hermes разобрал <b>{summary.aiAnalyzed ?? 0} из ${summary.active ?? 0}</b></span></div>
            </div>

            <section className="v2-ai-ticket-list">
                {items.length ? items.map((item) => <TicketSignalCard key={item.id} item={item}/>) : <div className="v2-ai-all-clear"><span>✓</span><div><h3>По этому сигналу всё спокойно</h3><p>Новых заявок для вмешательства руководителя нет.</p></div></div>}
            </section>
        </> : null}
    </div>;
}
